import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import { ref, get, set, update, onValue, off } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { useAuthContext } from "../../contexts/AuthContext";

// ─── Design tokens ────────────────────────────────────────────────────────────
const glassCardSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: "20px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(18px)",
};

const activeCardSx = {
  ...glassCardSx,
  background: "rgba(34,197,94,0.08)",
  border: "1px solid rgba(34,197,94,0.25)",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function genCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no ambiguous chars
  let code = "";
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

function formatEventDate(ms) {
  if (!ms) return "Data não definida";
  return new Date(Number(ms)).toLocaleDateString("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ─── Sub-component: copy button with "Copiado!" feedback ─────────────────────
function CopyButton({ text, label, icon: Icon = ContentCopyRoundedIcon, size = "small" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Button
      size={size}
      variant="outlined"
      startIcon={copied ? <DoneRoundedIcon /> : <Icon />}
      onClick={handleCopy}
      sx={{
        borderRadius: "10px",
        borderColor: copied ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.2)",
        color: copied ? "#4ade80" : "rgba(255,255,255,0.85)",
        minWidth: 130,
        transition: "all 0.2s",
        "&:hover": {
          borderColor: copied ? "rgba(34,197,94,0.7)" : "rgba(255,255,255,0.4)",
          background: "rgba(255,255,255,0.05)",
        },
      }}
    >
      {copied ? "Copiado!" : label}
    </Button>
  );
}

// ─── Sub-component: single event card ────────────────────────────────────────
function EventCheckinCard({ event, activeSession, onActivate, onDeactivate, activating, deactivating }) {
  const isActive = !!activeSession;
  const checkinUrl = activeSession
    ? `${window.location.origin}/checkin/scanner/${activeSession.code}`
    : "";

  return (
    <Paper sx={isActive ? activeCardSx : glassCardSx}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        {/* Left: event info */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          {event.bannerUrl ? (
            <Box
              component="img"
              src={event.bannerUrl}
              alt={event.title}
              sx={{
                width: 52,
                height: 52,
                borderRadius: "12px",
                objectFit: "cover",
                flexShrink: 0,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 52,
                height: 52,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.5)",
                flexShrink: 0,
              }}
            >
              <EventRoundedIcon />
            </Avatar>
          )}

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "rgba(255,255,255,0.95)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: { xs: 180, sm: 280 },
                }}
              >
                {event.title || "Sem título"}
              </Typography>

              <Chip
                size="small"
                label={event.status === "online" ? "Publicado" : "Rascunho"}
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor:
                    event.status === "online"
                      ? "rgba(0,255,180,0.1)"
                      : "rgba(255,255,255,0.08)",
                  color: event.status === "online" ? "#69f0ae" : "rgba(255,255,255,0.6)",
                }}
              />

              {isActive && (
                <Chip
                  size="small"
                  label="CHECK-IN ATIVO"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 800,
                    bgcolor: "rgba(34,197,94,0.18)",
                    color: "#4ade80",
                    letterSpacing: "0.04em",
                  }}
                />
              )}
            </Stack>

            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.45)", display: "block", mt: 0.25 }}
            >
              {formatEventDate(event.timing?.startAtMs)}
            </Typography>
          </Box>
        </Stack>

        {/* Right: actions */}
        <Box sx={{ flexShrink: 0 }}>
          {isActive ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
              {/* Code chip */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "10px",
                  bgcolor: "rgba(34,197,94,0.12)",
                  border: "1px solid rgba(34,197,94,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <QrCodeScannerRoundedIcon sx={{ fontSize: 16, color: "#4ade80" }} />
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 900,
                    fontSize: "1rem",
                    letterSpacing: "0.15em",
                    color: "#4ade80",
                  }}
                >
                  {activeSession.code}
                </Typography>
              </Box>

              <CopyButton text={activeSession.code} label="Copiar código" />

              <CopyButton
                text={checkinUrl}
                label="Copiar link"
                icon={LinkRoundedIcon}
              />

              <Button
                size="small"
                variant="contained"
                startIcon={
                  deactivating ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <StopCircleOutlinedIcon />
                  )
                }
                disabled={deactivating}
                onClick={() => onDeactivate(activeSession.code)}
                sx={{
                  borderRadius: "10px",
                  bgcolor: "rgba(239,68,68,0.8)",
                  "&:hover": { bgcolor: "rgba(239,68,68,1)" },
                  minWidth: 110,
                }}
              >
                {deactivating ? "Desativando..." : "Desativar"}
              </Button>
            </Stack>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={
                activating ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PlayCircleOutlinedIcon />
                )
              }
              disabled={activating}
              onClick={() => onActivate(event)}
              sx={{
                borderRadius: "10px",
                bgcolor: "rgba(34,197,94,0.85)",
                color: "#fff",
                fontWeight: 700,
                "&:hover": { bgcolor: "rgba(34,197,94,1)" },
                minWidth: 140,
              }}
            >
              {activating ? "Ativando..." : "Ativar Check-in"}
            </Button>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CheckinManager() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const organizerId = user?.uid || null;

  const [events, setEvents] = useState([]);
  const [sessions, setSessions] = useState({}); // { [eventId]: sessionObj }
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Per-event loading states
  const [activating, setActivating] = useState(null);   // eventId
  const [deactivating, setDeactivating] = useState(null); // code

  // ── Listen to organizer's events ──────────────────────────────────────────
  useEffect(() => {
    if (!organizerId) {
      setLoadingEvents(false);
      return;
    }

    const eventsRef = ref(rtdb, "events");
    const handler = (snap) => {
      const val = snap.val();
      const list = val
        ? Object.entries(val)
            .map(([id, data]) => ({ id, ...(data || {}) }))
            .filter((e) => e.organizerId === organizerId)
            .sort((a, b) => {
              const aMs = a.timing?.startAtMs ?? 0;
              const bMs = b.timing?.startAtMs ?? 0;
              return Number(aMs) - Number(bMs);
            })
        : [];
      setEvents(list);
      setLoadingEvents(false);
    };

    onValue(eventsRef, handler, () => {
      setEvents([]);
      setLoadingEvents(false);
    });

    return () => off(eventsRef, "value", handler);
  }, [organizerId]);

  // ── Listen to active sessions for this organizer ──────────────────────────
  useEffect(() => {
    if (!organizerId) {
      setLoadingSessions(false);
      return;
    }

    const sessRef = ref(rtdb, "checkinSessions");
    const handler = (snap) => {
      const val = snap.val();
      const map = {};
      if (val) {
        Object.values(val).forEach((s) => {
          if (s.organizerId === organizerId && s.active === true) {
            map[s.eventId] = s;
          }
        });
      }
      setSessions(map);
      setLoadingSessions(false);
    };

    onValue(sessRef, handler, () => {
      setSessions({});
      setLoadingSessions(false);
    });

    return () => off(sessRef, "value", handler);
  }, [organizerId]);

  // ── Activate session ──────────────────────────────────────────────────────
  const handleActivate = async (event) => {
    setActivating(event.id);
    try {
      const code = genCode();
      await set(ref(rtdb, `checkinSessions/${code}`), {
        code,
        eventId: event.id,
        eventTitle: event.title || "",
        eventBannerUrl: event.bannerUrl || event.banner?.url || "",
        organizerId,
        createdAt: Date.now(),
        active: true,
      });
    } catch (err) {
      console.error("Erro ao ativar check-in:", err);
    } finally {
      setActivating(null);
    }
  };

  // ── Deactivate session ────────────────────────────────────────────────────
  const handleDeactivate = async (code) => {
    setDeactivating(code);
    try {
      await update(ref(rtdb, `checkinSessions/${code}`), { active: false });
    } catch (err) {
      console.error("Erro ao desativar check-in:", err);
    } finally {
      setDeactivating(null);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const loading = loadingEvents || loadingSessions;
  const activeCount = Object.keys(sessions).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, margin: "0 auto" }}>
      {/* ── Header ── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em" }}
          >
            CONTROLE DE ENTRADA
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, color: "rgba(255,255,255,0.95)", lineHeight: 1.1 }}
          >
            Gerenciar Check-in
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.5)", mt: 0.5 }}
          >
            Ative ou desative sessões de check-in por evento. O código gerado é
            usado pelo scanner na entrada.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<QrCodeScannerRoundedIcon />}
          onClick={() => navigate("/checkin")}
          sx={{
            borderRadius: "12px",
            color: "rgba(255,255,255,0.85)",
            borderColor: "rgba(255,255,255,0.2)",
            fontWeight: 700,
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.05)" },
          }}
        >
          Abrir Scanner
        </Button>
      </Stack>

      {/* ── Summary chips ── */}
      {!loading && events.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
          <Chip
            icon={<EventRoundedIcon sx={{ fontSize: 16 }} />}
            label={`${events.length} evento${events.length !== 1 ? "s" : ""}`}
            sx={{
              bgcolor: "rgba(255,255,255,0.07)",
              color: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          />
          {activeCount > 0 && (
            <Chip
              icon={<QrCodeScannerRoundedIcon sx={{ fontSize: 16 }} />}
              label={`${activeCount} sessão${activeCount !== 1 ? "ões" : ""} ativa${activeCount !== 1 ? "s" : ""}`}
              sx={{
                bgcolor: "rgba(34,197,94,0.12)",
                color: "#4ade80",
                border: "1px solid rgba(34,197,94,0.25)",
                fontWeight: 700,
              }}
            />
          )}
        </Stack>
      )}

      {/* ── Loading state ── */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: "rgba(255,255,255,0.5)" }} />
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>
              Carregando eventos...
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── Empty state ── */}
      {!loading && events.length === 0 && (
        <Paper sx={{ ...glassCardSx, textAlign: "center", py: 6 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.3)",
              margin: "0 auto",
              mb: 2,
            }}
          >
            <EventRoundedIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "rgba(255,255,255,0.7)", mb: 1 }}
          >
            Nenhum evento encontrado
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.4)", mb: 3, maxWidth: 380, mx: "auto" }}
          >
            Crie seu primeiro evento para poder ativar uma sessão de check-in.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => navigate("/dashboard/eventos/novo")}
            sx={{ borderRadius: "12px", fontWeight: 700 }}
          >
            Criar evento
          </Button>
        </Paper>
      )}

      {/* ── Event list ── */}
      {!loading && events.length > 0 && (
        <Stack spacing={1.5}>
          {/* Active sessions first, then inactive */}
          {[
            ...events.filter((e) => !!sessions[e.id]),
            ...events.filter((e) => !sessions[e.id]),
          ].map((event) => (
            <EventCheckinCard
              key={event.id}
              event={event}
              activeSession={sessions[event.id] || null}
              onActivate={handleActivate}
              onDeactivate={handleDeactivate}
              activating={activating === event.id}
              deactivating={
                sessions[event.id]
                  ? deactivating === sessions[event.id].code
                  : false
              }
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
